import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useProfileState } from '@/contexts/ProfileContext';
import { cleanSearchTitle } from '@/lib/utils';
import { trackEvent, EVENTS, showQuickWinToast } from '@/lib/analytics';

// Each event type gets its own color treatment so the eye can scan the grid by category
const TYPE_STYLES = {
  conference: { bg: '#E0EEFB', color: '#0A66C2', label: 'Conference' },
  community:  { bg: '#ECE6FB', color: '#4B358B', label: 'Community' },
  course:     { bg: '#E9FAEC', color: '#1F4D2A', label: 'Course' },
  newsletter: { bg: '#FBF1DE', color: '#7A5B16', label: 'Newsletter' },
  meetup:     { bg: '#FBF1DE', color: '#7A5B16', label: 'Meetup' },
  default:    { bg: '#F5F7F5', color: '#7A7F7A', label: 'Resource' },
};

// The Sustainability Decoded newsletter is always shown as the first entry
// inside "Events & resources", regardless of what the analysis engine
// returns. Location-agnostic — this is the SD-Pivot house newsletter.
const SD_NEWSLETTER = {
  name: 'Sustainability Decoded',
  type: 'Newsletter',
  cost: 'Free',
  location: 'Global',
  when: 'Weekly',
  relevance: "The SD-Pivot house newsletter — weekly briefings on sustainability careers, market moves, credentials that matter, and the interviews and roles worth watching. Start here.",
  url: 'https://sustainabilitydecoded.com/',
  pinned: true,
};

function styleFor(rawType) {
  const key = (rawType || '').trim().toLowerCase();
  return TYPE_STYLES[key] || { ...TYPE_STYLES.default, label: rawType || 'Resource' };
}

function ExternalIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  );
}

export default function NetworkIn() {
  const { analysis, markQuickWin } = useProfileState();
  const network = analysis?.call2?.network || [];
  const allEvents = analysis?.call2?.events_resources || [];

  // If we're navigated to /dashboard/network#events-resources (from My
  // Progress' Quick Win deep-link), scroll the events grid into view once the
  // page has rendered.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash === '#events-resources') {
      // Give React one paint tick before scrolling.
      const t = setTimeout(() => {
        const el = document.getElementById('events-resources');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(t);
    }
  }, []);

  // Unified list: Sustainability Decoded is ALWAYS the first entry, followed
  // by the LLM's events/resources/newsletters (de-duped so if the model also
  // returned Sustainability Decoded we don't render it twice). No more split
  // between "Events & resources" and "Newsletters" — newsletters simply carry
  // the "Newsletter" tag and live in one grid.
  const events = useMemo(() => {
    const norm = (s) => String(s || '').trim().toLowerCase();
    const filtered = (allEvents || []).filter(
      (e) => !norm(e.name).includes('sustainability decoded')
    );
    return [SD_NEWSLETTER, ...filtered];
  }, [allEvents]);

  return (
    <div>
      <h1 className="text-[30px] font-semibold text-sd-black mb-2 heading-tight">Network In</h1>
      <p className="text-sd-mid-gray text-[14.5px] mb-7">The specific people, events and communities that will move your pivot forward.</p>

      <h2 className="text-[16px] font-semibold text-sd-black mb-3">People to meet</h2>
      <div className="space-y-3 mb-9">
        {network.map((p, i) => <PersonCard key={i} p={p} idx={i} />)}
        {network.length === 0 && <div className="text-sd-mid-gray text-[13.5px]">No connections in this analysis yet.</div>}
      </div>

      {events.length > 0 && (
        <>
          <h2 id="events-resources" className="text-[16px] font-semibold text-sd-black mb-3 scroll-mt-24">Events &amp; resources · {events.length}</h2>
          <p className="text-sd-mid-gray text-[13px] mb-3">Curated sustainability conferences, communities, courses and newsletters for you.</p>
          <div className="grid md:grid-cols-2 gap-3" data-testid="events-grid">
            {events.map((e, i) => <EventCard key={i} e={e} idx={i} onOpen={markQuickWin} />)}
          </div>
        </>
      )}
    </div>
  );
}

function EventCard({ e, idx, onOpen }) {
  const s = styleFor(e.type);
  const safeUrl = e.url && /^https?:\/\//i.test(e.url)
    ? e.url
    : `https://www.google.com/search?q=${encodeURIComponent(`${e.name} ${e.type || 'sustainability'}`)}`;

  const handleOpen = () => {
    // Fire the event AND the Quick Win auto-completion. Both non-blocking.
    try {
      trackEvent(EVENTS.resource_opened, {
        kind: String(e.kind || e.type || 'resource').slice(0, 24),
      });
      if (typeof onOpen === 'function') {
        onOpen('resource_opened').then((res) => {
          if (res && res.newly_completed) {
            showQuickWinToast('Explore one event or resource in your growth area');
          }
        }).catch(() => {});
      }
    } catch (_e) { /* no-op */ }
  };

  return (
    <div className="sd-card p-4 flex flex-col" data-testid={`event-${idx}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <span className="text-[14.5px] font-semibold text-sd-black">{e.name}</span>
        <span
          className="text-[10px] px-2 py-0.5 rounded-pill uppercase tracking-wider font-semibold"
          style={{ background: s.bg, color: s.color }}
          data-testid={`event-tag-${idx}`}
        >
          {s.label}
        </span>
      </div>
      <div className="text-[11.5px] text-sd-mid-gray">{[e.when, e.location, e.cost].filter(Boolean).join(' · ')}</div>
      {e.relevance && <p className="text-[12.5px] text-sd-dark-gray mt-2 leading-snug flex-1">{e.relevance}</p>}
      <div className="mt-3">
        <a
          href={safeUrl}
          target="_blank"
          rel="noreferrer"
          onClick={handleOpen}
          className="sd-btn-primary inline-flex items-center gap-2 !py-2 !px-3.5 !text-[12.5px]"
          data-testid={`event-open-${idx}`}
        >
          <ExternalIcon />
          Open
        </a>
      </div>
    </div>
  );
}

function PersonCard({ p, idx }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeVariant, setActiveVariant] = useState(0);

  // Backwards-compat: old analyses have `template` (string), new ones have `variants` (array).
  const variants = useMemo(() => {
    if (Array.isArray(p.variants) && p.variants.length) return p.variants;
    if (p.template) return [{ angle: 'value-first insight', message: p.template }];
    return [];
  }, [p]);

  const current = variants[activeVariant] || variants[0];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(current?.message || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Clipboard access denied (older browsers / iframe restrictions) — no-op
      // but log so we can spot when a real user hits this consistently.
      console.warn('[NetworkIn] clipboard.writeText denied:', err?.message || err);
    }
  };

  // Use the pure role title for LinkedIn people search — the archetype `name`
  // (e.g. "CSO at a listed FTSE-100 consumer-goods company") is a description,
  // not a searchable string.
  const searchQuery = cleanSearchTitle(p.role) || cleanSearchTitle(p.name) || 'sustainability';
  const searchLinkedIn = `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(searchQuery)}`;

  return (
    <div className="sd-card overflow-hidden">
      <button className="w-full p-4 text-left flex items-start justify-between gap-4" onClick={() => setOpen(o => !o)} data-testid={`network-${idx}`}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-sd-primary to-[#3FB854] text-white font-semibold flex items-center justify-center shrink-0">
            {(p.name || '?')[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-sd-black truncate">{p.name}</div>
            <div className="text-[12.5px] text-sd-mid-gray truncate">{p.role}</div>
            <div className="text-[11px] text-sd-mid-gray mt-0.5">{p.platform} · {p.connection_path}</div>
          </div>
        </div>
        <span className="text-sd-mid-gray shrink-0">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 animate-fade-in">
          {/* Why valuable + Outreach variants — side by side on desktop, stacked on mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 items-stretch">
            {/* Why valuable */}
            <div className="p-3 rounded-[10px] bg-sd-ivory text-[13px] text-sd-dark-gray leading-relaxed h-full">
              <div className="text-[11px] uppercase tracking-wider text-sd-mid-gray font-semibold mb-1">Why valuable</div>
              {p.why_valuable || '—'}
            </div>

            {/* Outreach variants — tabbed if multiple */}
            {variants.length > 0 && (
              <div className="rounded-[10px] bg-sd-cream flex flex-col h-full" data-testid={`network-outreach-${idx}`}>
                <div className="flex items-center justify-between px-3 pt-3 pb-2 gap-2 flex-wrap">
                  <div className="flex flex-wrap gap-1.5">
                    {variants.map((v, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setActiveVariant(i); }}
                        className={`text-[10.5px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-pill transition-colors ${
                          activeVariant === i
                            ? 'bg-sd-primary text-white'
                            : 'bg-white text-sd-mid-gray hover:text-sd-dark-gray'
                        }`}
                        data-testid={`network-variant-${idx}-${i}`}
                      >
                        {v.angle || `Angle ${String.fromCharCode(65 + i)}`}
                      </button>
                    ))}
                  </div>
                  <button
                    className="text-[11px] text-sd-primary font-medium hover:underline"
                    onClick={(e) => { e.stopPropagation(); copy(); }}
                    data-testid={`network-copy-${idx}`}
                  >
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <div className="px-3 pb-3 text-[13px] text-sd-dark-gray whitespace-pre-line leading-relaxed flex-1">
                  {current?.message || '—'}
                </div>
              </div>
            )}
          </div>

          <a
            href={searchLinkedIn}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="sd-btn-primary inline-flex items-center gap-2 !py-2 !px-3.5 !text-[12.5px]"
            data-testid={`network-find-${idx}`}
          >
            <ExternalIcon />
            Find on LinkedIn
          </a>
        </div>
      )}
    </div>
  );
}
